import '@fontsource-variable/jost';
import './styles.css';
import { Vector3 } from 'three';
import type { Object3D, PerspectiveCamera } from 'three';
import { GAME_NAME } from '@cloudpearl/config';
import { Game } from './game/Game';

const app = document.getElementById('app')!;

function fail(msg: string): void {
  app.innerHTML = `<div style="position:fixed;inset:0;display:grid;place-items:center;font-family:system-ui;color:#1d2b4f;background:#f6f8fb;text-align:center;padding:24px"><div><h2 style="letter-spacing:.3em;text-transform:uppercase;font-weight:500">${GAME_NAME}</h2><p>${msg}</p></div></div>`;
}

const probe = document.createElement('canvas').getContext('webgl2');
// Free the test context straight away (browsers cap live contexts; phones are short on memory).
probe?.getExtension('WEBGL_lose_context')?.loseContext();
if (!probe) {
  fail('This game needs WebGL2. Try a recent Chrome, Safari, Firefox or Edge.');
} else {
  const game = new Game(app);
  game.boot().catch((e: unknown) => {
    console.error(e);
    fail('Something went wrong while painting the world. Please reload.');
  });
  (window as unknown as { game: Game }).game = game;
}

// Dev-only QA helpers: drive frames deterministically and save canvas captures (see vite.config.ts).
if (import.meta.env.DEV) {
  const w = window as unknown as Record<string, unknown>;
  // ?bench: profiling in a hidden tab, where Chrome throttles timers to ~1/min. Short timeouts
  // (the loader's yields) run on a message channel instead so loading never stalls.
  if (new URLSearchParams(location.search).has('bench')) {
    const native = window.setTimeout.bind(window);
    const ch = new MessageChannel();
    const queue: (() => void)[] = [];
    ch.port1.onmessage = () => queue.shift()?.();
    (window as unknown as { setTimeout: unknown }).setTimeout = (fn: (...a: unknown[]) => void, ms = 0, ...args: unknown[]): number => {
      if (ms > 120) return native(fn, ms, ...args);
      queue.push(() => fn(...args));
      ch.port2.postMessage(0);
      return 0;
    };
  }
  const g = (): { last: number; frame(t: number): void; renderer: { canvas: HTMLCanvasElement } } => w['game'] as never;
  w['drive'] = (n: number, dt = 1000 / 60): void => {
    let t = g().last;
    for (let i = 0; i < n; i++) {
      t += dt;
      g().frame(t);
    }
  };
  w['snap'] = async (name: string): Promise<string> => {
    (w['drive'] as (n: number) => void)(1);
    const src = g().renderer.canvas;
    const c = document.createElement('canvas');
    c.width = 900;
    c.height = Math.round((900 * src.height) / src.width);
    c.getContext('2d')!.drawImage(src, 0, 0, c.width, c.height);
    const r = await fetch(`/__snap?name=${name}`, { method: 'POST', body: c.toDataURL('image/jpeg', 0.85) });
    return r.text();
  };
  // Render a free camera (eye → target) after letting the world stream around it, and save it.
  // e.g. await view('edge', [70, 4, -10], [60, 3, -14])
  w['view'] = async (name: string, eye: number[], target: number[], fov = 50, width = 800): Promise<string> => {
    const gm = w['game'] as never as {
      cam: { camera: PerspectiveCamera };
      world: { scene: Object3D; update(c: PerspectiveCamera, focus: Vector3, fwd: Vector3, dt: number): void };
      renderer: { canvas: HTMLCanvasElement; render(...a: unknown[]): void };
      post: unknown;
      time: number;
    };
    const c = gm.cam.camera.clone();
    c.fov = fov;
    c.position.set(eye[0]!, eye[1]!, eye[2]!);
    c.lookAt(target[0]!, target[1]!, target[2]!);
    c.updateProjectionMatrix();
    c.updateMatrixWorld();
    const focus = new Vector3(target[0]!, 0, target[2]!);
    const fwd = new Vector3(target[0]! - eye[0]!, 0, target[2]! - eye[2]!).normalize();
    for (let i = 0; i < 10; i++) {
      gm.world.update(c, focus, fwd, 0.2);
      await new Promise((r) => setTimeout(r, 150));
    }
    gm.renderer.render(gm.world.scene, c, gm.post, gm.time, 1 / 60);
    const src = gm.renderer.canvas;
    const out = document.createElement('canvas');
    out.width = width;
    out.height = Math.round((width * src.height) / src.width);
    out.getContext('2d')!.drawImage(src, 0, 0, out.width, out.height);
    const r = await fetch(`/__snap?name=${name}`, { method: 'POST', body: out.toDataURL('image/jpeg', 0.88) });
    return r.text();
  };
  // Capture the frame *with* the DOM overlay (menu / HUD / cards) — the browser pane is often
  // hidden, so screenshots are stale. The overlay is rasterised through an SVG foreignObject with
  // the page's CSS and fonts inlined; animations/transitions are frozen at their end state.
  let fontCss: string | null = null;
  const inlineFonts = async (): Promise<string> => {
    if (fontCss !== null) return fontCss;
    const out: string[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }
      for (const r of Array.from(rules)) {
        if (!(r instanceof CSSFontFaceRule)) continue;
        let css = r.cssText;
        for (const m of css.matchAll(/url\(["']?([^"')]+)["']?\)/g)) {
          const blob = await (await fetch(new URL(m[1]!, sheet.href ?? location.href))).blob();
          const data = await new Promise<string>((res) => {
            const fr = new FileReader();
            fr.onload = () => res(fr.result as string);
            fr.readAsDataURL(blob);
          });
          css = css.replace(m[0], `url(${data})`);
        }
        out.push(css);
      }
    }
    fontCss = out.join('\n');
    return fontCss;
  };
  w['snapUi'] = async (name: string, width = 900): Promise<string> => {
    const vw = innerWidth;
    const vh = innerHeight;
    // Grab the WebGL frame now: its drawing buffer is cleared once the page yields.
    const out = document.createElement('canvas');
    out.width = width;
    out.height = Math.round((width * vh) / vw);
    const ctx = out.getContext('2d')!;
    (w['drive'] as (n: number) => void)(1);
    ctx.drawImage(g().renderer.canvas, 0, 0, out.width, out.height);
    const css = Array.from(document.styleSheets)
      .flatMap((s) => {
        try {
          return Array.from(s.cssRules).filter((r) => !(r instanceof CSSFontFaceRule)).map((r) => r.cssText);
        } catch {
          return [];
        }
      })
      .join('\n');
    const freeze = '*,*::before,*::after{animation:none!important;transition:none!important}';
    const ui = document.querySelector('.ui')!.cloneNode(true) as HTMLElement;
    const body = `<div xmlns="http://www.w3.org/1999/xhtml" class="${document.body.className}" style="position:relative;width:${vw}px;height:${vh}px;font-family:'Jost Variable',system-ui,sans-serif"><style>${await inlineFonts()}\n${css}\n${freeze}</style>${new XMLSerializer().serializeToString(ui)}</div>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${vw}" height="${vh}"><foreignObject width="100%" height="100%">${body}</foreignObject></svg>`;
    const img = new Image();
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    await img.decode();
    ctx.drawImage(img, 0, 0, out.width, out.height);
    const r = await fetch(`/__snap?name=${name}`, { method: 'POST', body: out.toDataURL('image/jpeg', 0.88) });
    return r.text();
  };
  w['ready'] = (state = 'idle'): Promise<void> =>
    new Promise((res) => {
      const t = setInterval(() => {
        const gm = w['game'] as { state?: string } | undefined;
        if (gm?.state === state) {
          clearInterval(t);
          res();
        }
      }, 50);
    });
}
